/**
 * PaymentMethodIconsPanel — 支付方式图标配置（ADM-APP-S013）
 *
 * 契约源 dev-docs `02-API契约/A8-运营支付.md` §7.8.5~§7.8.7 + `03-数据字典/70-产品配置.md` §3.7.3。
 * 挂在 App 详情「支付渠道」tab，**排在策略面板之前** —— 契约的成员产生路径是「先配图标、再加成员」
 * （§7.8.2 图标前置校验），UI 次序照此排，运营才不会先撞 422 再回头找图标入口。
 *
 * **一行 = 一个可选支付方式**（A8 §7.8.8：ACTIVE 且平台适用的目录行 + 该 App 的 Stripe 同步取值）：
 * 图标按面板可选项配、不按结算通道配，各个不带固定图标的目录行与每个同步取值各配各的。客户端不再持有任何内置通道图标（dd#1051），C 端 `payment_methods[]`
 * 的 `icon_url` 恒非空下发 —— 没配图标的支付方式不能成为策略成员，配了才能上面板。
 *
 * **卡品牌不在本面板**（§7.8.8 `icon_fixed=true`，如 `STRIPE_VISA` / `PAYERMAX_VISA`）：用目录固定图标与底色，同一卡品牌
 * 在各收单供应商下同一套，不按 App 或供应商配置（ADM-APP-S013，70 §3.0 / §3.7.3）；§7.8.5 也不列它们。故不渲染行、
 * 不计「待补图标」，只在面板里列一行说明。它们仍是可选支付方式（不算「已退役 / 不可选」）。
 *
 * **「未配置」有两种物理形态**：整行缺失，或行在但 `icon_asset_id` 为 `null`。两者对「能不能进策略
 * 成员」等价，故本面板按 `icon_asset_id` 判、不按「列表里有没有这一行」判（服务端只返已存在的行）。
 *
 * **按平台渲染适用支付方式**（A8 §7.8 平台适用性 / 存量修复）：WEB 不提供 IAP 的上传入口；但 GET 不隐藏
 * 历史异常设置，WEB 已有非空 IAP 图标时仍渲染一条**只可清空**的异常行 —— 服务端只接受置 NULL，且须先
 * 从全部策略组移出 IAP，隐藏它会让这条异常配置永远清不掉。
 *
 * **写是两步**（§7.8.7 上传字节 → §7.8.6 挂标识），中间失败就是「资产已上传但没挂上」—— 资产不可变、
 * 不即时回收，重试再上传一份即可，不会覆盖任何既有图标。
 *
 * **清空受服务端成员前置校验管**（§7.8.6）：仍是任一策略组成员时清空即 422 —— 本面板**不据此拦截
 * 写入**（与服务端锁内判定必然有时间差，前端拦只会在竞态下给出错误结论），按钮照常可点、失败文案
 * 由服务端透出。tooltip 只**陈述当前已知的成员关系**（读到什么说什么，未就绪时不下结论），
 * 这与「不预判判决」不矛盾：陈述会过时，拦截会出错。
 *
 * **展示名与分组取自 §7.8.8**（`label` / `channel` / `group`），不在本仓维护：一级按结算通道，二级按目录
 * `group`（同步取值按同步可用性分「已启用 / 后台未启用」）。已有设置行但不在可选列表的取值（目录已退役等）
 * 收进末尾「已退役 / 不可选」组：服务端仍允许改它的图标与底色（A8 §7.8.6「已有的设置行」），只是不能再新建。
 *
 * **但成员关系仍要读，用途是「说清现状」而不是「代替服务端判决」**：expand 段的真实状态是「四个支付
 * 方式全是策略成员、一张图标都没配」，只看 settings 会把每一行都写成「暂不能加入策略成员」——
 * 对已经是成员的取值是事实反转。故读一份策略组，只用于行文案与顶部待办提示。
 *
 * **Stripe 同步（ADM-PAY-S013，A8 §7.8.9）挂在本面板头部**：打开面板（有写权限时）自动同步一次，另给手动
 * 同步按钮；同步只写同步表、不动成员与图标，失败保留上次结果并在状态行写明原因。同步取值标「Stripe 后台未启用」
 * 时仍可配图标、进策略（写侧不判同步可用性），只是 C 端暂不展示。
 */

import type {
  PaymentMethod,
  PaymentMethodOption,
  PaymentMethodOptionsResponse,
} from '@novel-admin/shared';
import { UpsertPaymentMethodSettingRequestSchema, isSettlementChannel } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { createPaymentMethodOptionLookup } from '~/data/adapters/payment-method-option-lookup';
import {
  paymentMethodOptionQueryKeys,
  paymentMethodSettingQueryKeys,
  usePaymentMethodOptions,
  usePaymentMethodPolicies,
  usePaymentMethodSettings,
} from '~/data/queries';
import { paymentMethodOptionsApi } from '~/hooks/use-payment-method-options';
import { paymentMethodSettingsApi } from '~/hooks/use-payment-method-settings';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { ConfirmDialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Section } from '~/ui/Section';
import { toast } from '~/ui/Toast';
import {
  NOT_SELECTABLE_BADGE,
  PAYMENT_METHOD_GROUP_TITLE,
  SETTLEMENT_CHANNEL_GROUP_TITLE,
} from './payment-method-labels';

/** 上传白名单与体积上限逐字取自 §7.8.7；前端先拦一道，服务端仍是权威。 */
const ACCEPTED_ICON_TYPES = ['image/png', 'image/webp'];
const MAX_ICON_BYTES = 512 * 1024;

const FORBIDDEN_ROLE_TITLE = 'viewer 只读 · 需 operator+ 权限';

/** 二级组键：目录 `group` / 未分组 / 同步取值按同步可用性 / 不在可选列表 */
type IconSubgroupKey =
  | 'UNGROUPED'
  | keyof typeof PAYMENT_METHOD_GROUP_TITLE
  | 'STRIPE_ENABLED'
  | 'STRIPE_DISABLED'
  | 'NOT_SELECTABLE';

const SUBGROUP_ORDER: readonly IconSubgroupKey[] = [
  'UNGROUPED',
  'WALLET',
  'CARD',
  'OTHER',
  'STRIPE_ENABLED',
  'STRIPE_DISABLED',
  'NOT_SELECTABLE',
];

const SUBGROUP_META: Record<IconSubgroupKey, { title?: string; hint?: string }> = {
  UNGROUPED: {},
  WALLET: { title: PAYMENT_METHOD_GROUP_TITLE.WALLET },
  CARD: { title: PAYMENT_METHOD_GROUP_TITLE.CARD },
  OTHER: { title: PAYMENT_METHOD_GROUP_TITLE.OTHER },
  STRIPE_ENABLED: {
    title: 'Stripe 已启用',
    hint: '从 Stripe 后台同步出的、当前已开启的支付方式',
  },
  STRIPE_DISABLED: {
    title: 'Stripe 后台未启用',
    hint: '成员与图标保留；在 Stripe 后台重新开启并同步后恢复下发',
  },
  NOT_SELECTABLE: {
    title: NOT_SELECTABLE_BADGE,
    hint: '不在可选列表（目录已退役或不适用本 App）：当前不下发，也不能再加入策略',
  },
};

/** 可选项 → 二级组：目录行按 `group`，同步行按同步可用性 */
function subgroupOfOption(option: PaymentMethodOption): IconSubgroupKey {
  if (option.source === 'STRIPE_SYNC') {
    return option.is_available === false ? 'STRIPE_DISABLED' : 'STRIPE_ENABLED';
  }
  return option.group ?? 'UNGROUPED';
}

/** 一级组标题：admin 已接入的结算通道取登记名，其余（新接入的框架通道）退回通道值 */
function channelGroupTitle(channel: string): string {
  return isSettlementChannel(channel) ? SETTLEMENT_CHANNEL_GROUP_TITLE[channel] : channel;
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : '操作失败';
}

/**
 * 同步失败 → 运营可执行的原因（A8 §7.8.9 失败表 + 21-Admin错误码段 §2.5）。按 code + `details.reason`
 * 分支，不解析 message；未登记的落回通用文案。
 */
export function stripeSyncFailureText(err: unknown): string {
  if (!(err instanceof AdminApiError)) return errMsg(err);
  const reason = err.details?.reason;
  switch (err.code) {
    case 'PAY_STRIPE_CHECKOUT_NOT_CONFIGURED':
      return reason === 'CREDENTIAL_ROUTE_MISSING'
        ? 'Stripe 凭据不可用，请在 Stripe Checkout 配置里重新提交 restricted key'
        : '该 App 尚未配置 Stripe Checkout 凭据';
    case 'PAY_STRIPE_CHECKOUT_NOT_READY':
      return '本环境未接入 Stripe 凭据加密密钥，请联系运维';
    case 'PAY_STRIPE_PAYMENT_METHOD_SYNC_FAILED':
      if (reason === 'PERMISSION_DENIED')
        return 'restricted key 缺少 Payment Method Configurations 读权限，请在 Stripe 补权限后重试';
      if (reason === 'DEFAULT_CONFIGURATION_MISSING')
        return 'Stripe 账户没有默认支付方式配置，请在 Stripe 设置后重试';
      return 'Stripe 拒绝了凭据，或凭据与当前环境不符';
    case 'RESOURCE_CONFLICT':
      return '同步期间 Stripe Checkout 配置发生变化，请重新同步';
    case 'SERVICE_UNAVAILABLE':
      return 'Stripe 暂时不可达，请稍后重试';
    default:
      return err.message;
  }
}

/** 写动作角色集，与 server route `requireRole('root','admin','operator')` 精确一致（前端只是 UX）。 */
function useCanWriteSetting(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

export function PaymentMethodIconsPanel({
  appId,
  isWeb = false,
}: {
  appId: string;
  isWeb?: boolean;
}) {
  const canWrite = useCanWriteSetting();
  const queryClient = useQueryClient();
  const { data: settings, isLoading, isError } = usePaymentMethodSettings(appId);
  const optionsQuery = usePaymentMethodOptions(appId);
  const options = optionsQuery.data;
  const optionLookup = createPaymentMethodOptionLookup(options);
  const labelOf = optionLookup.labelOf;
  // 可选支付方式（§7.8.8）：服务端已按目录状态与平台过滤（WEB 不含 IAP），这里对 WEB 的 IAP 再兜一次；
  // 未到达时不渲染任何行
  const applicableOptionRows = (options?.payment_methods ?? []).filter(
    (option) => !(isWeb && option.payment_method === 'IAP'),
  );
  // 「是否仍可选」按全部适用可选项判（含卡品牌）；图标行只渲染需要配置的（卡品牌用固定图标）
  const optionMethods = new Set(applicableOptionRows.map((option) => option.payment_method));
  const optionRows = applicableOptionRows.filter((option) => !option.icon_fixed);
  const fixedIconLabels = applicableOptionRows
    .filter((option) => option.icon_fixed)
    .map((option) => labelOf(option.payment_method));
  const supportedMethods: PaymentMethod[] = optionRows.map((o) => o.payment_method);
  const syncedAvailability = new Map(
    optionRows
      .filter((o) => o.source === 'STRIPE_SYNC')
      .map((o) => [o.payment_method, o.is_available]),
  );
  // 同时读策略成员：**当前 dev / prod 的真实状态就是「四个支付方式全是成员、一张图标都没配」**
  //（srv 0200 原位展开写进来的成员 + §3.7.3 expand 段尚未配图标）。只读 settings 的话，每一行都会
  // 显示「暂不能加入策略成员」—— 对已经是成员的取值是**事实反转**，运营据此以为可以先不管，而
  // ① 策略面板任何一次保存都会被 §7.8.2 图标前置校验拒掉，② srv 的 contract 段 migration 会在
  // 「不存在未配置图标的未软删策略成员」这条 fail-loud 断言上整体中止。
  const { data: policies, isSuccess: policiesLoaded } = usePaymentMethodPolicies(appId);
  const [clearTarget, setClearTarget] = useState<PaymentMethod | null>(null);
  /** 正在上传 / 落库的支付方式（逐行禁用，不整面板置灰）*/
  const [pendingMethod, setPendingMethod] = useState<PaymentMethod | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: paymentMethodSettingQueryKeys.byApp(appId) });

  // 上传字节（§7.8.7）→ 挂标识（§7.8.6）。两步都失败于第一步时不会产生任何 DB 变更。
  const uploadMutation = useMutation({
    mutationFn: async (vars: { method: PaymentMethod; file: File }) => {
      const uploaded = await paymentMethodSettingsApi.uploadIcon(vars.file);
      return paymentMethodSettingsApi.upsert({
        app_id: appId,
        payment_method: vars.method,
        icon_asset_id: uploaded.icon_asset_id,
      });
    },
    onSuccess: async (saved) => {
      toast(`${labelOf(saved.payment_method)} · 图标已更新`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      await invalidate();
    },
    onError: async (err) => {
      toast(`图标保存失败 · ${errMsg(err)}`, { tone: 'neg', icon: 'IconAlert' });
      // 失败可能发生在「已上传、挂载失败」之后 —— 重取服务端真值，别让 UI 停在乐观态
      await invalidate();
    },
    onSettled: () => setPendingMethod(null),
  });

  // 清空（§7.8.6 `icon_asset_id: null`）：仍是策略成员时服务端 422，文案原样透出
  const clearMutation = useMutation({
    mutationFn: (method: PaymentMethod) =>
      paymentMethodSettingsApi.upsert({
        app_id: appId,
        payment_method: method,
        icon_asset_id: null,
        ...(isWeb && method === 'IAP' ? { icon_background_color: null } : {}),
      }),
    onSuccess: async (saved) => {
      toast(`${labelOf(saved.payment_method)} · 图标已清空`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      setClearTarget(null);
      await invalidate();
    },
    onError: async (err) => {
      toast(`清空图标失败 · ${errMsg(err)}`, { tone: 'neg', icon: 'IconAlert' });
      setClearTarget(null);
      await invalidate();
    },
    onSettled: () => setPendingMethod(null),
  });

  const backgroundMutation = useMutation({
    mutationFn: async (input: { method: PaymentMethod; color: string | null }) => {
      // 底色单独修改仍须携带当前图标；绕过页面缓存，避免写回其他会话已替换的旧图标。
      const currentSettings = await paymentMethodSettingsApi.list(appId);
      const currentSetting = currentSettings.find(
        (setting) => setting.payment_method === input.method,
      );
      return paymentMethodSettingsApi.upsert({
        app_id: appId,
        payment_method: input.method,
        icon_asset_id: currentSetting?.icon_asset_id ?? null,
        icon_background_color: input.color,
      });
    },
    onSuccess: async () => {
      toast('底色已保存', { tone: 'pos', icon: 'IconCheck' });
      await invalidate();
    },
    onError: async (error) => {
      toast(`底色保存失败 · ${errMsg(error)}`, { tone: 'neg', icon: 'IconAlert' });
      await invalidate();
    },
  });
  // 未启用组默认折叠：这些取值当前不下发，运营多数时候不需要看
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(
    new Set<string>(['STRIPE:STRIPE_DISABLED']),
  );
  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const busy = uploadMutation.isPending || clearMutation.isPending || backgroundMutation.isPending;
  const iconOf = (method: PaymentMethod) =>
    settings.find((row) => row.payment_method === method) ?? null;
  // 成员判定跨全部未软删组（全局 + 任一 Region 覆盖），与服务端 §7.8.6 的判定域一致
  const memberMethods = new Set(
    policies.flatMap((policy) => policy.payment_methods.map((item) => item.payment_method)),
  );
  const missingIconMembers = supportedMethods.filter(
    (method) => memberMethods.has(method) && (iconOf(method)?.icon_asset_id ?? null) === null,
  );
  // WEB 的非空 IAP 图标是待清理的历史异常：保留只可清空的行，不进「待补图标」统计
  const hasLegacyWebIapIcon =
    isWeb &&
    ((iconOf('IAP')?.icon_asset_id ?? null) !== null ||
      (iconOf('IAP')?.icon_background_color ?? null) !== null);
  const isLegacyWebIap = (method: PaymentMethod) => isWeb && method === 'IAP';
  // 已有设置行但不在可选列表的取值（§7.8.8「已退役 / 不可选」）：只在列表已到达时才能下此结论。
  // WEB 的 IAP 走上面的「异常历史配置」只可清空行，已清空的 IAP 行不再渲染。
  const notSelectableMethods = optionLookup.ready
    ? settings
        .map((row) => row.payment_method)
        .filter((method) => !optionMethods.has(method))
        .filter((method) => !isLegacyWebIap(method) || hasLegacyWebIapIcon)
    : [];

  // 全局策略组成员的顺序只用来排「同组内」的行次序；下发预览与调序入口在「支付通道策略」面板。
  const globalOrder = (policies.find((p) => p.region_code === null)?.payment_methods ?? []).map(
    (item) => item.payment_method,
  );
  // 两级分组：结算通道（可选列表出现次序，即目录 catalog_order）→ 二级组；组内全局组成员按下发顺序在前，
  // 其余保持原次序在后。不在可选列表的行查不到通道，单列一组排在最后。
  const entries: { channel: string; subgroup: IconSubgroupKey; method: PaymentMethod }[] = [
    ...optionRows.map((option) => ({
      channel: option.channel,
      subgroup: subgroupOfOption(option),
      method: option.payment_method,
    })),
    ...notSelectableMethods.map((method) => ({
      channel: NOT_SELECTABLE_BADGE,
      subgroup: 'NOT_SELECTABLE' as const,
      method,
    })),
  ];
  const channelOrder = [...new Set(entries.map((entry) => entry.channel))];
  const groupedChannels = channelOrder.map((channel) => {
    const subgroups = SUBGROUP_ORDER.map((key) => {
      const inGroup = entries
        .filter((entry) => entry.channel === channel && entry.subgroup === key)
        .map((entry) => entry.method);
      const ordered = inGroup
        .filter((method) => globalOrder.includes(method))
        .sort((a, b) => globalOrder.indexOf(a) - globalOrder.indexOf(b));
      return {
        // 折叠状态按「通道:组」记，同名二级组在不同通道下互不影响
        key: `${channel}:${key}`,
        // 不在可选列表那一组已用组名作一级标题，二级不再重复标题
        ...(key === 'NOT_SELECTABLE' ? { hint: SUBGROUP_META[key].hint } : SUBGROUP_META[key]),
        negative: key === 'STRIPE_DISABLED' || key === 'NOT_SELECTABLE',
        methods: [...ordered, ...inGroup.filter((method) => !globalOrder.includes(method))],
      };
    }).filter((subgroup) => subgroup.methods.length > 0);
    return {
      channel,
      title: channel === NOT_SELECTABLE_BADGE ? NOT_SELECTABLE_BADGE : channelGroupTitle(channel),
      subgroups,
    };
  });

  const onPick = (method: PaymentMethod, file: File) => {
    if (!ACCEPTED_ICON_TYPES.includes(file.type)) {
      toast('图标仅支持 PNG / WebP', { tone: 'neg', icon: 'IconAlert' });
      return;
    }
    if (file.size > MAX_ICON_BYTES) {
      toast(`图标不得超过 512KB（当前 ${Math.ceil(file.size / 1024)}KB）`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      return;
    }
    setPendingMethod(method);
    uploadMutation.mutate({ method, file });
  };

  return (
    <Section
      title="支付方式图标"
      sub="底色可选，未配置按白色显示，策略成员也可清空底色。客户端不再内置任何通道图标：每个不带固定图标的可选支付方式都要配图标，配了才能加进通道策略成员（图标按支付方式配，钱包与同步出的支付方式各配各的）；卡品牌使用系统固定图标与底色，不在此配置。"
    >
      <StripePaymentMethodSyncBar
        appId={appId}
        canWrite={canWrite}
        options={options}
        optionsError={optionsQuery.isError}
        onRetryOptions={optionsQuery.refetch}
      />
      {isLoading && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
          图标设置加载中…
        </div>
      )}
      {isError && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--neg)' }}>
          图标设置加载失败
        </div>
      )}
      {!isLoading && !isError && policiesLoaded && missingIconMembers.length > 0 && (
        // 存量成员待补图标是 §3.7.3 expand 段的**待办**，不是「配错了」——文案给动作，不报错
        <div
          style={{
            margin: '10px 0 2px',
            padding: '10px 12px',
            borderRadius: 7,
            background: 'var(--warn-soft)',
            color: 'var(--warn)',
            fontSize: 11.5,
            lineHeight: 1.6,
          }}
        >
          <strong>{missingIconMembers.length} 个策略成员还没有图标</strong>（
          {missingIconMembers.map((m) => labelOf(m)).join('、')}
          ）。在配齐图标前，下方「支付通道策略」里
          <strong>任何包含它们的那一组</strong>
          都存不下去（服务端只校验本次提交的成员，不含它们的组照常可存）；C
          端也拿不到这些支付方式的图标。请逐个上传。
        </div>
      )}
      {!isLoading && !isError && fixedIconLabels.length > 0 && (
        <div style={{ padding: '10px 0 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
          卡品牌使用系统固定图标与底色，无需配置、可直接加入策略：{fixedIconLabels.join('、')}
        </div>
      )}
      {!isLoading && !isError && options === null && !optionsQuery.isError && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
          可选支付方式加载中…
        </div>
      )}
      {!isLoading &&
        !isError &&
        groupedChannels.map((channel) => (
          <div
            key={channel.channel}
            className="card-flat"
            style={{ marginTop: 14, overflow: 'hidden' }}
          >
            <div
              className="hstack"
              style={{
                gap: 8,
                padding: '10px 14px',
                background: 'var(--bg-subtle)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-strong)' }}>
                {channel.title}
              </span>
              <span className="chip" style={{ fontSize: 10 }}>
                {channel.subgroups.reduce((total, subgroup) => total + subgroup.methods.length, 0)}
              </span>
            </div>
            {channel.subgroups.map((subgroup) => {
              // 只有带标题的二级组可折叠（无标题 = 渠道下只有一组，没有折叠把手）
              const collapsed = subgroup.title !== undefined && collapsedGroups.has(subgroup.key);
              const negative = subgroup.negative;
              return (
                <div key={subgroup.key}>
                  {!subgroup.title && subgroup.hint && (
                    <div
                      style={{
                        padding: '8px 14px 0',
                        fontSize: 11,
                        color: 'var(--fg-muted)',
                      }}
                    >
                      {subgroup.hint}
                    </div>
                  )}
                  {subgroup.title && (
                    <button
                      type="button"
                      className="focusable"
                      aria-expanded={!collapsed}
                      onClick={() => toggleGroup(subgroup.key)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 14px',
                        border: 0,
                        borderBottom: '1px solid var(--border)',
                        borderLeft: `3px solid ${negative ? 'var(--neg)' : 'var(--accent)'}`,
                        background: 'var(--bg-muted)',
                        font: 'inherit',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      {collapsed ? (
                        <Icons.IconChevRight size={12} />
                      ) : (
                        <Icons.IconChevDown size={12} />
                      )}
                      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--fg-strong)' }}>
                        {subgroup.title}
                      </span>
                      <span
                        className={negative ? 'chip chip-neg' : 'chip'}
                        style={{ fontSize: 10 }}
                      >
                        {subgroup.methods.length}
                      </span>
                      {subgroup.hint && (
                        <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                          {subgroup.hint}
                        </span>
                      )}
                    </button>
                  )}
                  {!collapsed && (
                    <div style={{ padding: '0 14px' }}>
                      {subgroup.methods.map((method) => (
                        <IconRow
                          key={`${method}:${iconOf(method)?.icon_background_color ?? ''}`}
                          backgroundColor={iconOf(method)?.icon_background_color ?? null}
                          onSaveBackground={(color) => backgroundMutation.mutate({ method, color })}
                          label={labelOf(method)}
                          notSelectable={
                            optionLookup.ready &&
                            !optionMethods.has(method) &&
                            !isLegacyWebIap(method)
                          }
                          iconUrl={iconOf(method)?.icon_url ?? null}
                          isMember={policiesLoaded ? memberMethods.has(method) : null}
                          syncedAvailable={syncedAvailability.get(method) ?? null}
                          legacyClearOnly={isLegacyWebIap(method)}
                          canWrite={canWrite}
                          busy={busy}
                          pending={pendingMethod === method}
                          onPick={(file) => onPick(method, file)}
                          onClear={() => setClearTarget(method)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

      {clearTarget && (
        <ConfirmDialog
          title={
            isLegacyWebIap(clearTarget)
              ? `清理 ${labelOf(clearTarget)} 的历史配置？`
              : `清空 ${labelOf(clearTarget)} 的图标？`
          }
          message={
            isLegacyWebIap(clearTarget)
              ? 'Web App 不支持 IAP：清空后这条异常历史配置即清理完成，图标与底色同时清空，之后不能再为 IAP 配置图标或底色。'
              : '清空后该支付方式即为「未配置图标」，不能再被加进任何策略成员集合。'
          }
          details={[
            '若它当前仍是某个策略组的成员，服务端会拒绝本次清空 —— 请先去下方「支付通道策略」把它移出全部成员集合。',
            ...(isLegacyWebIap(clearTarget)
              ? []
              : ['换图标不必先清空：直接上传新图即可，旧资产至少继续可访问 24 小时。']),
          ]}
          tone="warn"
          confirmLabel="确认清空"
          onConfirm={() => {
            setPendingMethod(clearTarget);
            clearMutation.mutate(clearTarget);
          }}
          onClose={() => setClearTarget(null)}
        />
      )}
    </Section>
  );
}

/**
 * Stripe 支付方式同步条（A8 §7.8.9）：左侧状态（当前 Account / 上次同步时间 / 最近失败原因），右侧手动同步。
 *
 * **打开面板自动同步一次**（契约「打开支付方式配置页时自动同步」），仅限有写权限的账号 —— 端点是
 * operator+ mfa，viewer 只读 §7.8.8。两个前置：
 * - **等 §7.8.8 读到再发**：与 GET 并发时，晚到的 GET 会把同步结果覆盖回旧值；
 * - **该 App 已有 Stripe Checkout 配置才自动发**（`stripe_account_id` 非空）：没配置的同步必然
 *   NOT_CONFIGURED，而失败的写请求按 P0 审计规则恒落一条尝试行 —— 每次打开都白落一条。手动同步不受此限。
 *   「不发」不占自动同步名额：缓存的旧可选项可能早于配置，重取到配置后照样自动同步。
 * 自动同步失败只进状态行、不弹 toast；手动同步的失败才 toast。失败不改可选项缓存（服务端保留上次结果），
 * 成功先取消在途的 §7.8.8 读取、再写回缓存。
 */
function StripePaymentMethodSyncBar({
  appId,
  canWrite,
  options,
  optionsError,
  onRetryOptions,
}: {
  appId: string;
  canWrite: boolean;
  options: PaymentMethodOptionsResponse | null;
  /** §7.8.8 读取失败：同步取值的图标行此时不渲染，必须明说并给重试，不能显示成「加载中」 */
  optionsError: boolean;
  onRetryOptions: () => void;
}) {
  const queryClient = useQueryClient();
  const [lastFailure, setLastFailure] = useState<string | null>(null);
  const syncMutation = useMutation({
    mutationFn: (_vars: { manual: boolean }) => paymentMethodOptionsApi.syncStripe(appId),
    onSuccess: async (data, vars) => {
      // 在途的 GET 可能读于同步提交之前、晚于本次返回落地 —— 先取消，免得旧结果覆盖新结果
      await queryClient.cancelQueries({ queryKey: paymentMethodOptionQueryKeys.byApp(appId) });
      queryClient.setQueryData(paymentMethodOptionQueryKeys.byApp(appId), data);
      setLastFailure(null);
      if (vars.manual) toast('Stripe 支付方式已同步', { tone: 'pos', icon: 'IconCheck' });
    },
    onError: (err, vars) => {
      const text = stripeSyncFailureText(err);
      setLastFailure(text);
      if (vars.manual) toast(`同步失败 · ${text}`, { tone: 'neg', icon: 'IconAlert' });
    },
  });
  const { mutate } = syncMutation;
  // 每个 App 只自动同步一次（StrictMode 双调用 / 重渲染不重复打 Stripe）。**真发起时才记账**：缓存里的
  // 旧可选项可能还是「无配置」，后台重取到新配置后须仍能触发，不能被那次「不发」占掉名额。
  const autoSyncedAppRef = useRef<string | null>(null);
  const hasStripeConfig = options?.stripe_account_id != null;
  useEffect(() => {
    if (!canWrite || !hasStripeConfig || autoSyncedAppRef.current === appId) return;
    autoSyncedAppRef.current = appId;
    setLastFailure(null);
    mutate({ manual: false });
  }, [appId, canWrite, hasStripeConfig, mutate]);

  const status = syncMutation.isPending
    ? '正在从 Stripe 同步支付方式…'
    : options === null
      ? optionsError
        ? '可选支付方式读取失败：图标行暂不显示'
        : '同步结果加载中…'
      : !hasStripeConfig
        ? '该 App 尚未配置 Stripe Checkout，不自动同步'
        : options.synced_at === null
          ? '尚无 Stripe 同步结果'
          : `上次同步 ${fmtDateTime(options.synced_at)}`;
  return (
    <FormRow
      label="Stripe 同步支付方式"
      hint="从该 App 的 Stripe 账户读取默认支付方式配置；卡与 Apple Pay / Google Pay 已由固定选项覆盖，不重复同步。同步不改策略成员与图标。"
    >
      <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
          {options?.stripe_account_id ? (
            <>
              <span className="mono">{options.stripe_account_id}</span> ·{' '}
            </>
          ) : null}
          {status}
        </span>
        {options === null && optionsError && (
          <button type="button" className="btn btn-sm" onClick={onRetryOptions}>
            重新读取
          </button>
        )}
        {lastFailure !== null && !syncMutation.isPending && (
          <span style={{ fontSize: 11.5, color: 'var(--warn)' }}>
            最近一次同步失败：{lastFailure}（已保留上次结果）
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn-sm"
          disabled={!canWrite || syncMutation.isPending}
          title={canWrite ? '立即从 Stripe 重新同步' : FORBIDDEN_ROLE_TITLE}
          onClick={() => mutate({ manual: true })}
        >
          <Icons.IconRefresh size={11} />
          {syncMutation.isPending ? '同步中…' : '同步'}
        </button>
      </div>
    </FormRow>
  );
}

/** 单个支付方式行：预览 + 上传 / 换图 + 清空；WEB 的历史 IAP 图标只给清空。 */
function IconRow({
  label,
  notSelectable,
  iconUrl,
  backgroundColor,
  onSaveBackground,
  isMember,
  syncedAvailable,
  legacyClearOnly,
  canWrite,
  busy,
  pending,
  onPick,
  onClear,
}: {
  /** 展示名（§7.8.8 `label` / 同步行类型键；不在可选列表退回原始取值） */
  label: string;
  /** 已有设置行但不在可选列表（目录已退役等）：可改图标与底色，不能再加入策略 */
  notSelectable: boolean;
  iconUrl: string | null;
  backgroundColor: string | null;
  onSaveBackground: (color: string | null) => void;
  /** 是否是本 App 任一未软删策略组的成员；`null` = 策略数据未就绪，此时不给成员相关结论 */
  isMember: boolean | null;
  /** Stripe 同步取值的同步可用性（§7.8.8 `is_available`）；固定子集为 `null` */
  syncedAvailable: boolean | null;
  /** WEB 不适用的历史 IAP 图标：服务端只接受置 NULL，故不提供上传 / 更换 */
  legacyClearOnly: boolean;
  canWrite: boolean;
  busy: boolean;
  pending: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const configured = iconUrl !== null;
  const [backgroundDraft, setBackgroundDraft] = useState(backgroundColor ?? '');
  const parsedBackground =
    UpsertPaymentMethodSettingRequestSchema.shape.icon_background_color.safeParse(backgroundDraft);
  // 预览白色由 C 端契约指定，与后台明暗主题无关。
  const previewBackground = parsedBackground.success
    ? backgroundDraft
    : (backgroundColor ?? '#FFFFFF');
  // 四象限各自的后果都不同，不要合并成「配了 / 没配」两句：
  // 已配 + 成员 = 正常态；已配 + 非成员 = 可随时加入；未配 + 成员 = **待补**（保存会被拒）；
  // 未配 + 非成员 = 加不进去。策略未就绪时退回只讲图标本身，不编造成员结论。
  // 异常历史行只讲清理顺序：仍是成员时清空会被拒，须先移出全部策略组。
  const hint = legacyClearOnly
    ? isMember === true
      ? 'Web App 不支持 IAP · 异常历史配置待清理：IAP 仍在策略组里，请先在下方「支付通道策略」把它移出全部策略组，再清空图标'
      : 'Web App 不支持 IAP · 异常历史配置待清理：确认已从全部策略组移出 IAP 后清空图标'
    : configured
      ? isMember === true
        ? '已配置图标 · 已是策略成员'
        : isMember === false
          ? '已配置图标 · 可加入策略成员'
          : '已配置图标'
      : isMember === true
        ? '⚠️ 已是策略成员但未配图标 —— 策略保存会被拒，请尽快上传'
        : isMember === false
          ? '未配置图标 · 暂不能加入策略成员'
          : '未配置图标';

  return (
    <FormRow label={label} hint={hint}>
      <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
        {configured ? (
          // 图标走 C 端公开静态路由（无鉴权），admin 直接加载预览，不另设后台读取端点（§7.8.5）
          <img
            draggable={false}
            src={iconUrl}
            alt={`${label} 图标`}
            style={{
              width: 28,
              height: 28,
              objectFit: 'contain',
              border: '1px solid var(--border)',
              borderRadius: 6,
              background: previewBackground,
            }}
          />
        ) : (
          <span className="chip chip-warn" style={{ fontSize: 10 }}>
            {isMember === true ? '待补图标' : '未配置'}
          </span>
        )}
        {legacyClearOnly && (
          <span className="chip chip-neg" style={{ fontSize: 10 }}>
            异常历史配置
          </span>
        )}
        {notSelectable && (
          <span
            className="chip chip-neg"
            style={{ fontSize: 10 }}
            title="不在可选支付方式列表（目录已退役或不适用本 App）：当前不下发，不能再加入策略；仍可改图标与底色"
          >
            {NOT_SELECTABLE_BADGE}
          </span>
        )}
        {/* 同步不可用：Stripe 侧已关闭，或是切换账户前的同步结果 —— 成员与图标保留，只是不下发 */}
        {syncedAvailable === false && (
          <span
            className="chip chip-neg"
            style={{ fontSize: 10 }}
            title="Stripe 后台未开启该支付方式，或它来自切换账户前的同步；成员与图标保留，重新开启并同步后恢复下发，当前 C 端不展示"
          >
            Stripe 后台未启用
          </span>
        )}
        <div style={{ flex: 1 }} />
        {!legacyClearOnly && (
          <>
            <input
              type="color"
              className="swatch-input"
              aria-label={`${label} 底色选择器`}
              value={previewBackground}
              disabled={!canWrite || busy}
              onChange={(event) => setBackgroundDraft(event.target.value.toUpperCase())}
              style={{
                width: 28,
                height: 28,
                padding: 0,
                border: '1px solid var(--border)',
                borderRadius: 6,
              }}
            />
            <input
              className="input mono"
              aria-label={`${label} 底色`}
              placeholder="#RRGGBB"
              value={backgroundDraft}
              disabled={!canWrite || busy}
              onChange={(event) => setBackgroundDraft(event.target.value.toUpperCase())}
              style={{ width: 100 }}
            />
            <button
              type="button"
              className="btn btn-sm"
              aria-label={`保存 ${label} 底色`}
              disabled={
                !canWrite ||
                busy ||
                !parsedBackground.success ||
                backgroundDraft === backgroundColor
              }
              onClick={() => onSaveBackground(backgroundDraft)}
            >
              保存底色
            </button>
          </>
        )}
        {!legacyClearOnly && (
          <button
            type="button"
            className="btn btn-sm"
            aria-label={`清空 ${label} 底色`}
            disabled={!canWrite || busy || backgroundColor === null}
            title={canWrite ? '恢复默认白色，不影响图标与策略成员资格' : FORBIDDEN_ROLE_TITLE}
            onClick={() => onSaveBackground(null)}
          >
            清空底色
          </button>
        )}
        {!legacyClearOnly && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_ICON_TYPES.join(',')}
              style={{ display: 'none' }}
              aria-label={`选择 ${label} 图标文件`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // 清空 input：同一个文件连选两次时 change 不再触发，运营会以为「点了没反应」
                e.target.value = '';
                if (file) onPick(file);
              }}
            />
            <button
              type="button"
              className="btn btn-sm"
              aria-label={`${configured ? '更换' : '上传'} ${label} 图标`}
              disabled={!canWrite || busy}
              title={canWrite ? 'PNG / WebP，≤ 512KB' : FORBIDDEN_ROLE_TITLE}
              onClick={() => inputRef.current?.click()}
            >
              <Icons.IconUpload size={11} />
              {pending ? '上传中…' : configured ? '更换图标' : '上传图标'}
            </button>
          </>
        )}
        {(configured || legacyClearOnly) && (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            aria-label={legacyClearOnly ? `清理 ${label} 历史配置` : `清空 ${label} 图标`}
            disabled={!canWrite || busy}
            title={
              !canWrite
                ? FORBIDDEN_ROLE_TITLE
                : isMember === true
                  ? '它仍是策略成员 —— 服务端会拒绝清空，请先把它移出全部策略组'
                  : isMember === false
                    ? legacyClearOnly
                      ? '清理 Web App 不适用的历史 IAP 图标'
                      : '清空后不能再加进策略成员'
                    : // 策略数据未就绪：不下任何成员结论（它可能正是成员，服务端会拒）
                      '清空前请确认它已不在任何策略组里 —— 仍是成员时服务端会拒绝'
            }
            onClick={onClear}
          >
            <Icons.IconTrash size={11} />
            {legacyClearOnly ? '清理历史配置' : '清空'}
          </button>
        )}
      </div>
    </FormRow>
  );
}
